import clsx from "clsx";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import type { ReactNode, ThHTMLAttributes } from "react";

export function Table({ children, className, caption }: { children: ReactNode; className?: string; caption?: string }) {
  return (
    <div className={clsx("overflow-x-auto rounded-xl border border-border bg-surface", className)}>
      <table className="w-full border-collapse text-sm">
        {caption && <caption className="sr-only">{caption}</caption>}
        {children}
      </table>
    </div>
  );
}

export function THead({ children }: { children: ReactNode }) {
  return (
    <thead className="sticky top-0 z-10 bg-surface text-left text-xs font-medium text-ink-3 shadow-[inset_0_-1px_0_var(--grid)]">
      {children}
    </thead>
  );
}

export function Th({ className, align = "left", ...rest }: ThHTMLAttributes<HTMLTableCellElement> & { align?: "left" | "right" }) {
  return (
    <th
      scope="col"
      className={clsx("px-3 py-2.5 font-medium", align === "right" && "text-right", className)}
      {...rest}
    />
  );
}

export type SortDir = "asc" | "desc" | null;

/** Sortable column header: a real button, with aria-sort on the cell. */
export function SortableTh({ label, dir, onSort, align = "left" }: {
  label: string;
  dir: SortDir;
  onSort: () => void;
  align?: "left" | "right";
}) {
  const Icon = dir === "asc" ? ArrowUp : dir === "desc" ? ArrowDown : ArrowUpDown;
  return (
    <th
      scope="col"
      aria-sort={dir === "asc" ? "ascending" : dir === "desc" ? "descending" : "none"}
      className={clsx("px-3 py-1.5 font-medium", align === "right" && "text-right")}
    >
      <button
        type="button"
        onClick={onSort}
        className={clsx(
          "inline-flex items-center gap-1 rounded px-1 py-1 hover:text-ink",
          dir && "text-ink",
          align === "right" && "flex-row-reverse",
        )}
      >
        {label}
        <Icon className={clsx("size-3.5", !dir && "opacity-40")} aria-hidden />
      </button>
    </th>
  );
}

export function Td({ className, align = "left", ...rest }: React.TdHTMLAttributes<HTMLTableCellElement> & { align?: "left" | "right" }) {
  return (
    <td
      className={clsx("px-3 py-2.5 align-middle", align === "right" && "text-right num", className)}
      {...rest}
    />
  );
}

export function Tr({ className, ...rest }: React.HTMLAttributes<HTMLTableRowElement>) {
  return <tr className={clsx("border-t border-line first:border-t-0", className)} {...rest} />;
}
